import type { RouteRecord } from "vite-react-ssg";
import RootLayout from "./components/RootLayout";
import { asanaData } from "@/data/asanas-v2/index";
import { getBibliographySlugs } from "@/data/bibliography/index";
import { getGlossarySlugs } from "@/data/glossary/index";
import LegacyRedirectElement from "@/components/LegacyRedirectElement";
// Helper to wrap default exports as Component
const lazyPage = (importFn: () => Promise<{ default: React.ComponentType }>) => {
  return async () => {
    const module = await importFn();
    return { Component: module.default };
  };
};

// Static paths for Asana library
const getAsanaSlugs = () => Object.keys(asanaData);

// Static paths for yoga articles
const getYogaArticleSlugs = () => [
  "yoga-und-bluthochdruck",
  "yoga-medizinische-forschung",
  "yoga-krebs-onkologie",
  "viniyoga-und-spiritualitaet",
];

// Static paths for consulting articles
const getConsultingArticleSlugs = () => [
  "personzentrierte-supervision-hospiz-palliativ",
];

// Legacy redirect mappings (client-side fallback for server redirects)
type LegacyRedirect = { from: string; to: string };
const legacyRedirects: LegacyRedirect[] = [
  { from: "vita", to: "/zur-person" },
  { from: "online-angebot", to: "/mensch-im-fokus/praesenz-und-online" },
  { from: "gestalttherapie", to: "/mensch-im-fokus/gestaltpsychotherapie" },
  { from: "gestalttherapie/einzeltherapie", to: "/mensch-im-fokus/gestaltpsychotherapie" },
  { from: "gestalttherapie/paartherapie", to: "/mensch-im-fokus/beratung-paare-familien" },
  { from: "gestalttherapie/familientherapie", to: "/mensch-im-fokus/beratung-paare-familien" },
  { from: "yoga", to: "/mensch-im-fokus/yoga" },
  { from: "yoga/yoga-einzeltraining", to: "/mensch-im-fokus/yoga/einzelunterricht" },
  { from: "yoga/yoga-workshops-old", to: "/mensch-im-fokus/yoga/seminare" },
  { from: "yoga/yoga-fuer-krebspatientinnen", to: "/mensch-im-fokus/yoga/onko-yoga" },
  { from: "mensch-im-fokus/yoga/krankheit", to: "/mensch-im-fokus/yoga/onko-yoga" },
  { from: "mensch-im-fokus/yoga/onko-yoga-entwurf", to: "/mensch-im-fokus/yoga/onko-yoga" },
  { from: "onko-yoga", to: "/mensch-im-fokus/yoga/onko-yoga" },
  { from: "atemuebungen-im-yoga", to: "/mensch-im-fokus/yoga/wissen" },
  { from: "yoga-und-bluthochdruck", to: "/mensch-im-fokus/yoga/wissen/artikel/yoga-und-bluthochdruck" },
  { from: "artikel", to: "/mensch-im-fokus/yoga/wissen" },
  { from: "supervision", to: "/consulting/supervision" },
  { from: "teamentwicklung", to: "/consulting/supervision" },
  { from: "strategieentwicklung", to: "/consulting/strategieentwicklung" },
  { from: "burnoutvorsorge-nachsorge", to: "/consulting/coaching" },
  { from: "referenzen", to: "/consulting" },
  { from: "blog", to: "/consulting/wissen" },
];

// Generate redirect routes
const redirectRoutes: RouteRecord[] = legacyRedirects.map(({ from, to }) => ({
  path: from,
  element: <LegacyRedirectElement to={to} />,
}));

export const routes: RouteRecord[] = [
  {
    path: "/",
    Component: RootLayout,
    children: [
      // Legacy redirects (client-side fallback)
      ...redirectRoutes,

      // Homepage
      {
        index: true,
        lazy: lazyPage(() => import("./pages/Index")),
      },

      // Consulting branch
      {
        path: "consulting",
        lazy: lazyPage(() => import("./pages/Consulting")),
      },
      {
        path: "consulting/supervision",
        lazy: lazyPage(() => import("./pages/ConsultingSupervision")),
      },
      {
        path: "consulting/coaching",
        lazy: lazyPage(() => import("./pages/ConsultingCoaching")),
      },
      {
        path: "consulting/strategieentwicklung",
        lazy: lazyPage(() => import("./pages/ConsultingStrategieentwicklung")),
      },
      {
        path: "consulting/lehrsupervision",
        lazy: lazyPage(() => import("./pages/ConsultingLehrsupervision")),
      },
      {
        path: "consulting/wissen",
        lazy: lazyPage(() => import("./pages/ConsultingWissen")),
      },
      {
        path: "consulting/wissen/:slug",
        lazy: lazyPage(() => import("./pages/ConsultingArtikelDetail")),
        entry: "src/pages/ConsultingArtikelDetail.tsx",
        getStaticPaths: () => getConsultingArticleSlugs().map(slug => `consulting/wissen/${slug}`),
      },

      // Mensch im Fokus branch
      {
        path: "mensch-im-fokus",
        lazy: lazyPage(() => import("./pages/MenschImFokus")),
      },
      {
        path: "mensch-im-fokus/gestaltpsychotherapie",
        lazy: lazyPage(() => import("./pages/Gestaltpsychotherapie")),
      },
      {
        path: "mensch-im-fokus/gestaltpsychotherapie/wissen",
        lazy: lazyPage(() => import("./pages/GestaltpsychotherapieVertiefung")),
      },
      {
        path: "mensch-im-fokus/beratung-paare-familien",
        lazy: lazyPage(() => import("./pages/BeratungPaareFamilien")),
      },
      {
        path: "mensch-im-fokus/beratung-paare-familien/wissen",
        lazy: lazyPage(() => import("./pages/BeratungPaareFamilienVertiefung")),
      },

      // Yoga branch
      {
        path: "mensch-im-fokus/yoga",
        lazy: lazyPage(() => import("./pages/Yoga")),
      },
      {
        path: "mensch-im-fokus/yoga/einzelunterricht",
        lazy: lazyPage(() => import("./pages/YogaEinzelunterricht")),
      },
      {
        path: "mensch-im-fokus/yoga/gruppen",
        lazy: lazyPage(() => import("./pages/YogaGruppen")),
      },
      {
        path: "mensch-im-fokus/yoga/onko-yoga",
        lazy: lazyPage(() => import("./pages/YogaOnkoYoga")),
      },
      {
        path: "mensch-im-fokus/yoga/seminare",
        lazy: lazyPage(() => import("./pages/YogaSeminarSylt")),
      },
      {
        path: "mensch-im-fokus/yoga/supervision",
        lazy: lazyPage(() => import("./pages/YogaSupervision")),
      },
      {
        path: "mensch-im-fokus/yoga/fortbildung",
        lazy: lazyPage(() => import("./pages/YogaFortbildung")),
      },

      // Yoga Wissen (knowledge) pages
      {
        path: "mensch-im-fokus/yoga/wissen",
        lazy: lazyPage(() => import("./pages/Wissen")),
      },
      // Fachartikel listing page
      {
        path: "mensch-im-fokus/yoga/wissen/artikel",
        lazy: lazyPage(() => import("./pages/FachartikelPage")),
      },
      // Asana Library
      {
        path: "mensch-im-fokus/yoga/wissen/asanas",
        lazy: lazyPage(() => import("./pages/AsanaLibraryPage")),
      },
      {
        path: "mensch-im-fokus/yoga/wissen/asanas/:slug",
        lazy: lazyPage(() => import("./pages/AsanaDetailPage")),
        entry: "src/pages/AsanaDetailPage.tsx",
        getStaticPaths: () => getAsanaSlugs().map(slug => `mensch-im-fokus/yoga/wissen/asanas/${slug}`),
      },
      {
        path: "mensch-im-fokus/yoga/wissen/artikel/:slug",
        lazy: lazyPage(() => import("./pages/ArtikelDetail")),
        entry: "src/pages/ArtikelDetail.tsx",
        getStaticPaths: () => getYogaArticleSlugs().map(slug => `mensch-im-fokus/yoga/wissen/artikel/${slug}`),
      },
      // Literaturverzeichnis
      {
        path: "mensch-im-fokus/yoga/wissen/literatur",
        lazy: lazyPage(() => import("./pages/LiteraturPage")),
      },
      {
        path: "mensch-im-fokus/yoga/wissen/literatur/:refId",
        lazy: lazyPage(() => import("./pages/LiteraturDetail")),
        entry: "src/pages/LiteraturDetail.tsx",
        getStaticPaths: () => getBibliographySlugs().map(slug => `mensch-im-fokus/yoga/wissen/literatur/${slug}`),
      },
      // Glossar
      {
        path: "mensch-im-fokus/yoga/wissen/glossar",
        lazy: lazyPage(() => import("./pages/GlossarPage")),
      },
      {
        path: "mensch-im-fokus/yoga/wissen/glossar/:termId",
        lazy: lazyPage(() => import("./pages/GlossarDetail")),
        entry: "src/pages/GlossarDetail.tsx",
        getStaticPaths: () => getGlossarySlugs().map(slug => `mensch-im-fokus/yoga/wissen/glossar/${slug}`),
      },
      // Utility pages
      {
        path: "kontakt/",
        lazy: lazyPage(() => import("./pages/Kontakt")),
      },
      {
        path: "mensch-im-fokus/praesenz-und-online",
        lazy: lazyPage(() => import("./pages/PraesenzUndOnline")),
      },
      {
        path: "zur-person",
        lazy: lazyPage(() => import("./pages/ZurPerson")),
      },
      {
        path: "impressum",
        lazy: lazyPage(() => import("./pages/Impressum")),
      },
      {
        path: "datenschutz",
        lazy: lazyPage(() => import("./pages/Datenschutz")),
      },

      // Admin tools (temporary)
      {
        path: "admin/image-optimizer",
        lazy: lazyPage(() => import("./pages/AdminImageOptimizer")),
      },

      // 404 catch-all
      {
        path: "*",
        lazy: lazyPage(() => import("./pages/NotFound")),
      },
    ],
  },
];
